// src/components/ResourceFormWrapper/ResourceFormWrapper.tsx
import type { LoadableComponent } from '@loadable/component';
import React, { useEffect, useMemo } from 'react';

import { useAutoCuration } from '@hooks/useAutoCuration';
import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';

import type { FormType } from '@utils/fhir-mappers';
import { mapToAttachmentName } from '@utils/mapBBoxTitleToFormType';
import { mergeOcrFromConfig } from '@utils/ocr/mergeFromConfig';

type Props = {
  discern_result_id?: string | null;
  record: any;
  formType: FormType;
  FormComponent: React.FC<any> | LoadableComponent<any>;
  patientId?: string | null;

  /**
   * Whether this form is being rendered inside the PDF auto-curation flow.
   * If true:
   *  - We still enter "creating" state on mount (startAddingRecord + setRecord)
   *  - BUT we do NOT call finishAddingRecord() on unmount, to avoid redirects.
   */
  isAutoCurating?: boolean;
  bboxId: string;
  pdfUrl?: string | null;
  nextId: string | null;
  onSubmit?: () => void;
  onCompleted?: () => void;
};

const ResourceFormWrapper: React.FC<Props> = ({
  discern_result_id,
  record,
  bboxId,
  nextId,
  formType,
  FormComponent,
  patientId,
  pdfUrl,
  onSubmit,
  onCompleted,
  isAutoCurating = false,
}) => {
  const {
    state: { record: contextRecord },
    startAddingRecord,
    setRecord,
    finishAddingRecord,
  } = useCuration();

  const { setRecord: setAutoCurationRecord } = useAutoCuration();
  const {
    state: { activeProfile, isCurator },
  } = useSession();

  const wrappedRecord = useMemo(() => {
    if (!record) return null;

    // Merge OCR in a generic way for OCR-enabled resources
    const mergeResult = mergeOcrFromConfig(formType, record) || {};

    const id = record?.id ?? record?.identifier ?? null;
    const attachments = [];

    if (record?.attachments && Array.isArray(record.attachments)) {
      attachments.push(...record.attachments);
    }
    const fileName = (() => {
      let name = mapToAttachmentName(formType);
      const priorities = ['startDate', 'testDate', 'date'];
      const keys = Object.keys(mergeResult);
      const dateValue = priorities
        .map((term) => keys.find((k) => k.toLowerCase().includes(term)))
        .find((result) => result !== undefined);
      const lastNameValue = activeProfile?.last_name.toLowerCase();

      if (dateValue && mergeResult[dateValue]) {
        name += `_${String(mergeResult[dateValue]).replace(/\s+/g, '-')}`;
      }
      if (lastNameValue) {
        name += `-${lastNameValue}`;
      }

      return `${name}.pdf`;
    })();

    if (pdfUrl) {
      attachments.push({
        contentType: 'application/pdf',
        url: pdfUrl,
        title: fileName,
      });
    }

    const base = {
      ...record,
      ...mergeResult,
      discern_result_id,
      bboxId,
      id,
      nextId,
      formType,
      attachments: attachments.length > 0 ? attachments : undefined,
      getId: () => id,
      getResourceId: () => id,
      isShareable: () => true,

      getDisplayName: () =>
        mergeResult.title ||
        record?.title ||
        mergeResult.procedureName ||
        record?.procedureName ||
        formType,

      /**
       * Generic model-input behavior:
       * - If the underlying record implements getModelInput(values),
       *   delegate to it (normal hydrated FHIR record).
       * - If not (auto-curation + plain object), simply return values;
       *   the drilldown can then wrap this via its template helpers.
       */
      getModelInput: (values: any) => {
        if (record && typeof record.getModelInput === 'function') {
          return record.getModelInput(values);
        }
        return values;
      },
    };

    return base;
  }, [
    record,
    formType,
    pdfUrl,
    discern_result_id,
    bboxId,
    nextId,
    activeProfile?.last_name,
  ]);
  const hasMountedRef = React.useRef(false);
  useEffect(() => {
    if (!wrappedRecord) return;

    startAddingRecord();
    setRecord(wrappedRecord);
    setAutoCurationRecord(wrappedRecord);
    hasMountedRef.current = true;
    if (!isAutoCurating) {
      return () => {
        finishAddingRecord();
      };
    }

    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrappedRecord, isAutoCurating, contextRecord]);

  useEffect(() => {
    return () => {
      if (hasMountedRef.current) {
        finishAddingRecord();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!wrappedRecord) return null;

  const submitCallback = onSubmit ?? onCompleted;

  return (
    <FormComponent
      data={wrappedRecord}
      isCreating
      isCurator={isCurator}
      isEditing
      patientId={patientId}
      disableNavigateOnCreate
      onSubmit={submitCallback}
      type={formType}
    />
  );
};

export default ResourceFormWrapper;
